import { Button, List, ListItem, ListItemText, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import type { OrganizationInfo } from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import LoadingFailedAlert from "../../common/LoadingFailedAlert";
import OrganizationSelector from "../../common/OrganizationSelector";
import PageTitle from "../../common/PageTitle";

const syncFundAdminBcPermissions = withErrorHandling(api.clientProvisioning.syncFundAdminBcPermissions);

const FundAdminBcPermissionsSync = () => {
  const [organization, setOrganization] = useState<OrganizationInfo | null>(null);
  const { executionState, setExecutionState } = useExecutionState();

  const [organizations, fetchOrganizationsError, { isFetching }] = useFetch(
    api.clientProvisioning.getExistingOrganizations
  );

  if (fetchOrganizationsError) {
    logError(fetchOrganizationsError, "[FundAdminBcPermissionsSync] getExistingOrganizations");
    return <LoadingFailedAlert apiError={fetchOrganizationsError} />;
  }

  if (organizations === undefined || isFetching) {
    return <InlineLoader />;
  }

  const managedFundManagerClients =
    organization?.managesClients.filter((client) => client.type === "FundManager") ?? [];
  const isProcessing = executionState.status === "in-progress";
  const canSync = organization !== null && !isProcessing;

  const handleSync = async () => {
    if (!organization) {
      return;
    }

    setExecutionState({ status: "in-progress" });

    const [, error] = await syncFundAdminBcPermissions({
      organizationClientCode: organization.clientCode,
    });

    if (error) {
      logError(error, "[FundAdminBcPermissionsSync] syncFundAdminBcPermissions");
      setExecutionState({ status: "error", error });
      return;
    }

    const details = [
      `Organization: ${organization.name} (${organization.clientCode})`,
      `Fund Managers synced: ${managedFundManagerClients.length}`,
      ...managedFundManagerClients.map((client) => `${client.name} (${client.clientCode})`),
    ];

    setExecutionState({
      status: "success",
      text: "BC permissions synchronized successfully",
      details,
    });
  };

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "32rem" }}>
      <PageTitle title="Fund Admin BC Permissions Sync" subtitle="Sync BC permissions for all organization companies">
        Select a Fund Admin organization and run force synchronization for all related Fund Manager clients.
        <br />
        <br />
        The backend loads organization data from Core and synchronizes permissions user-by-user for each related BC
        company.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <OrganizationSelector
          organizations={organizations}
          value={organization}
          onChange={(value) => {
            setOrganization(value);
            if (!isProcessing) {
              setExecutionState({ status: "idle" });
            }
          }}
        />
        <FieldHelp>Select Fund Admin organization to synchronize all related Fund Manager BC permissions.</FieldHelp>
      </Stack>
      {organization && (
        <Stack spacing={1} sx={{ width: "100%" }}>
          <Typography variant="subtitle2">Related Fund Managers ({managedFundManagerClients.length})</Typography>
          <List dense sx={{ width: "100%", bgcolor: "background.paper", border: 1, borderColor: "divider", py: 0 }}>
            {managedFundManagerClients.map((client) => (
              <ListItem key={client.id} divider>
                <ListItemText primary={client.name} secondary={client.clientCode} />
              </ListItem>
            ))}
          </List>
        </Stack>
      )}
      <Button variant="contained" onClick={handleSync} disabled={!canSync}>
        Sync BC Permissions
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default FundAdminBcPermissionsSync;
